import {
  type AccordionItemContentProps,
  type AccordionItemProps,
  type AccordionItemTriggerProps,
  Accordion as AccordionPrimitive,
} from "@ark-ui/react";
import { CaretDown, Info } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import { Tag, type TagProps } from "../tag/Tag.js";
import { Text } from "../text/Text.js";
import { Tooltip } from "../tooltip/Tooltip.js";

interface ExpandableTriggerProps
  extends Omit<AccordionItemTriggerProps, "title"> {
  title: React.ReactNode;
  tooltip?: React.ReactNode;
  tag?: string;
  tagProps?: Omit<TagProps, "children">;
  icon?: React.ReactNode;
  body?: React.ReactNode;
}

const Expandable = AccordionPrimitive.Root;

const ExpandableItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={clsx("ox-reset ox-ExItem", className)}
    {...props}
  />
));

const ExpandableTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.ItemTrigger>,
  ExpandableTriggerProps
>(({ className, title, tooltip, body, tag, tagProps, icon, ...props }, ref) => (
  <AccordionPrimitive.ItemTrigger
    ref={ref}
    className={clsx("ox-reset ox-ExTrigger", className)}
    {...props}
  >
    <div className="ox-ExTriggerContent">
      {icon ? <span className="ox-ExTriggerIcon">{icon}</span> : null}
      <div className="ox-ExTriggerTitle">
        <Text size="lg" fontWeight="semibold">
          {title}
        </Text>
        {tag ? <Tag {...tagProps}>{tag}</Tag> : null}
        {tooltip ? (
          <Tooltip lazyMount unmountOnExit openDelay={200} content={tooltip}>
            <span role="button" className="ox-ExTriggerTooltip">
              <Info size={16} />
            </span>
          </Tooltip>
        ) : null}
      </div>

      {body ? <div className="ox-reset ox-ExTriggerBody">{body}</div> : null}
    </div>
    <AccordionPrimitive.ItemIndicator>
      <CaretDown size={24} className="ox-ExTriggerIndicator" />
    </AccordionPrimitive.ItemIndicator>
  </AccordionPrimitive.ItemTrigger>
));

const ExpandableContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.ItemContent>,
  AccordionItemContentProps
>(({ children, className, ...props }, ref) => (
  <AccordionPrimitive.ItemContent
    ref={ref}
    className={clsx("ox-reset ox-ExContent", className)}
    {...props}
  >
    <div className={clsx("ox-reset ox-ExContentInner", className)}>
      {children}
    </div>
  </AccordionPrimitive.ItemContent>
));

const ExpandableSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={clsx("ox-reset ox-ExContentSection", className)}
    {...props}
  />
));

Expandable.displayName = "Expandable";
ExpandableItem.displayName = "ExpandableItem";
ExpandableTrigger.displayName = "ExpandableTrigger";
ExpandableContent.displayName = "ExpandableContent";
export {
  Expandable,
  ExpandableItem,
  ExpandableTrigger,
  ExpandableContent,
  ExpandableSection,
};
